import React, { useState, useEffect, useCallback } from "react";
import NavItem from "./NavItem";
import NavSpacer from "./NavSpacer";

import { NavInterface } from "../types/NavInterface";
import { DataInterface } from "../types/DataInterface";

const FORWARD = "forward";
const BACKWARD = "backward";

const Nav = ({
  hotspotData,
  requestedIndex,
  selectedIndex,
  prevIndex,
  handleHotspotClicked,
  setPrevIndex,
  isAnimating,
  setIsAnimating,
  isIntro,
}: NavInterface) => {
  const [data, setData] = useState<DataInterface[] | []>([]);

  const [navNode, setNavNode] = useState<HTMLDivElement | null>(null);
  const [containerNode, setContainerNode] = useState<HTMLUListElement | null>(
    null
  );

  const containerRef = useCallback((node: HTMLUListElement) => {
    if (node !== null) {
      setContainerNode(node);
    }
  }, []);

  const navRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setNavNode(node);
    }
  }, []);

  useEffect(() => {
    setData(hotspotData);
  }, [hotspotData]);

  useEffect(() => {
    if (!navNode || !containerNode) return;

    const navWidth = navNode.getBoundingClientRect().width;
    const containerWidth = containerNode.getBoundingClientRect().width;
    const scrollPos = ((containerWidth - navWidth) * requestedIndex) / 4;
    navNode.scrollTo({
      top: 0,
      left: scrollPos,
      behavior: "smooth",
    });
  }, [navNode, containerNode, requestedIndex]);

  const renderNavigation = () => {
    let navItems = [];
    let shouldAnimate = prevIndex >= 0 && prevIndex !== requestedIndex;
    let direction: "forward" | "backward" = "forward";
    let steps = 0;

    if (shouldAnimate) {
      direction = prevIndex < requestedIndex ? FORWARD : BACKWARD;
      steps = Math.abs(prevIndex - requestedIndex);
    }

    if (!data) return;

    const leni = data.length;

    let item;
    for (let i = 0; i < leni; i++) {
      const itemData = data[i];
      const requested = i === requestedIndex;

      item = (
        <NavItem
          key={i.toString()}
          label={itemData.nav}
          selected={requested}
          onItemClicked={() => {
            handleIndexRequested(i);
          }}
        />
      );
      navItems.push(item);

      if (i !== leni - 1) {
        const spacerKey = `${i}-spacer`;
        let animate = false;
        let step = 0;
        if (shouldAnimate) {
          if (direction === FORWARD) {
            animate = i >= prevIndex && i < requestedIndex;
            if (animate) step = i - prevIndex + 1;
          } else {
            animate = i >= requestedIndex && i < prevIndex;
            if (animate) step = requestedIndex - i + steps;
          }
        }
        item = (
          <NavSpacer
            key={spacerKey}
            animate={animate}
            direction={direction}
            step={step}
            totalItems={leni}
          />
        );
        navItems.push(item);
      }
    }

    return navItems;
  };

  const handleIndexRequested = (index: number) => {
    if (index === selectedIndex || isAnimating) return;

    setIsAnimating(true);
    setPrevIndex(selectedIndex);
    handleHotspotClicked(index);
  };

  return (
    <div className={`navigation ${isIntro ? 'is-intro' : ''}`} ref={navRef}>
      <div className="nav-wrapper">
        <ul className="items-container" ref={containerRef}>
          {renderNavigation()}
        </ul>
      </div>
    </div>
  );
};

export default Nav;
